import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select } from 'antd';
import PropTypes from 'prop-types';
import { Button, ListTableView } from 'components';
import SearchSelect from 'components/search-select';
import useRollbarNotifier from 'customHooks/useRollbarNotifier';
import { positionsApi } from 'api/positions';
import { capitalize } from 'utils/formatter/string';

// Normalize a position (list row) into the list of active users assigned to it.
// Prefers the multi-user `users` array (exposed by the backend); falls back to
// the single representative scalar for positions/backends that don't send it.
const getPositionUsers = (position) => {
  if (position?.users && position.users.length > 0) {
    return position.users;
  }
  if (position?.user_email) {
    return [{ user_email: position.user_email, user_name: position.user_name }];
  }
  return [];
};

function SwitchUserModal({ visible, close, onOk, selectedPosition, form }) {
  const [loading, setLoading] = useState(false);
  const [newPosition, setNewPosition] = useState(null);
  // Default the source user to the representative (first = most-recently
  // assigned, matching the backend ordering).
  const [sourceUser, setSourceUser] = useState(() => getPositionUsers(selectedPosition)[0] || null);
  const { getFieldDecorator } = form;
  const sendError = useRollbarNotifier();

  const sourceUsers = getPositionUsers(selectedPosition);
  const hasMultipleSourceUsers = sourceUsers.length > 1;

  // Re-sync the default source user whenever the modal opens for a (possibly
  // different) position, since this component stays mounted across opens.
  useEffect(() => {
    setSourceUser(getPositionUsers(selectedPosition)[0] || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPosition, visible]);

  const searchPositionApi = async (search) => {
    const res = await positionsApi.listPositions({ search, role: '', page: 1, per_page: 10 });
    const options = [];
    res.data.data.forEach((dt) => {
      // When the position exposes multiple active users, create one option per user
      // so the operator can pick exactly which occupant to move.
      const users = getPositionUsers(dt);
      if (users.length > 0) {
        users.forEach((u) => {
          options.push({
            text: users.length > 1 ? `${dt.position_label} — ${u.user_email}` : dt.position_label,
            value: JSON.stringify({ id: dt.position_id, email: u.user_email, name: u.user_name }),
          });
        });
      } else {
        options.push({
          text: dt.position_label,
          value: JSON.stringify({ id: dt.position_id, email: dt.user_email, name: dt.user_name }),
        });
      }
    });
    return options;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await form.validateFields();

    try {
      setLoading(true);
      // user_email_1 is omitted when the source position is vacant, letting the
      // backend "move" the destination user into it (matches pre-existing
      // behaviour). For a multi-user source it is always set (defaults to the
      // representative), so the operator never silently switches the wrong user.
      await positionsApi.switchPosition(
        selectedPosition.position_id,
        newPosition.id,
        sourceUser?.user_email,
        newPosition.email,
      );
      setLoading(false);
      setNewPosition(null);
      setSourceUser(null);
      close();
      Modal.success({
        title: 'Success switch position',
        content: `Success`,
        onOk,
      });
    } catch (error) {
      setLoading(false);
      // The backend surfaces a readable message (e.g. user_not_on_position,
      // user_email_required_for_multi_user) which the global error interceptor
      // already shows to the operator; keep the modal open so they can adjust.
      sendError('Error when switch position', error);
    }
    setLoading(false);
  };

  const onCancel = () => {
    setNewPosition(null);
    setSourceUser(null);
    close();
  };

  return (
    <Modal
      title={<span style={{ fontWeight: 700 }}>Switch Position</span>}
      destroyOnClose
      maskClosable={false}
      onOk={handleSubmit}
      onCancel={onCancel}
      visible={visible}
      footer={null}
      centered
    >
      <Form onSubmit={handleSubmit}>
        <Form.Item label="Position Label">
          {getFieldDecorator('currentPositionLabel', {
            initialValue: selectedPosition?.position_label,
          })(<Input placeholder="Position Label" disabled />)}
        </Form.Item>

        {hasMultipleSourceUsers ? (
          <Form.Item label="User to Switch" required>
            <Select
              placeholder="Select user to switch"
              value={sourceUser?.user_email}
              onChange={(email) => setSourceUser(sourceUsers.find((u) => u.user_email === email) || null)}
            >
              {sourceUsers.map((u) => (
                <Select.Option key={u.user_email} value={u.user_email}>
                  {`${capitalize(u.user_name || '-')} — ${u.user_email}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        ) : (
          <>
            <Form.Item label="Full Name">
              <Input placeholder="Name" value={sourceUser?.user_name} disabled />
            </Form.Item>
            <Form.Item label="Email">
              <Input placeholder="Email Address" value={sourceUser?.user_email} disabled />
            </Form.Item>
          </>
        )}

        <Form.Item label="New Position">
          {getFieldDecorator('position_id_2', {
            rules: [{ required: true, message: 'Please select new position' }],
          })(
            <SearchSelect
              loading={loading}
              wordLimit={3}
              placeholder="Search"
              isEffect
              onChange={(position) => setNewPosition(JSON.parse(position.value))}
              api={searchPositionApi}
            />,
          )}
        </Form.Item>
        {newPosition && (
          <div className="bg-yellow-100 pt-2 pb-4 px-4 rounded-lg -mt-5">
            <ListTableView
              className="w-1/2"
              loading={loading}
              data={{
                'Name ': capitalize(newPosition?.name || '-'),
                'Email ': capitalize(newPosition?.email || '-'),
              }}
            />
          </div>
        )}

        <div className="flex mt-5">
          <Button className="mr-3 ml-auto" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            Switch Position
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

SwitchUserModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  onOk: PropTypes.func.isRequired,
  selectedPosition: PropTypes.object,
  form: PropTypes.object.isRequired,
};

SwitchUserModal.defaultProps = {
  selectedPosition: {},
};

export default Form.create()(SwitchUserModal);
